/**
 * Value labels on the WHERE / HAVING pills — `o.customer_id = 42 — Acme Corp`,
 * `o.status_id = '3' — Active`: the label the column's chip shows for the
 * value (`FilterValueLabel`), after the condition as written. One column
 * resolver per clause card (`FilterPillLabels`), shared by its flat pills and
 * Rail leaves; a pill shows nothing extra while its label loads or when none
 * resolves.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { FilterValueLabel } from '../../../common/components/filters/FilterValueLabel';
import type { VqeEditContext } from '../../edit/editContext';
import { enrichParsedCondition, resolveEditPredicate } from '../common/looseFkEditEnrichment';
import { useVqeFilterColumns, type VqeFilterColumns } from '../common/useVqeFilterColumns';

const PillColumns = createContext<VqeFilterColumns | null>(null);

function PillColumnsScope({ editContext, children }: { readonly editContext: VqeEditContext; readonly children: ReactNode }) {
  return <PillColumns.Provider value={useVqeFilterColumns(editContext)}>{children}</PillColumns.Provider>;
}

/** Lets the pills under it label their values; without an edit context they show the SQL only. */
export function FilterPillLabels({ editContext, children }: { readonly editContext?: VqeEditContext; readonly children: ReactNode }) {
  return editContext ? <PillColumnsScope editContext={editContext}>{children}</PillColumnsScope> : <>{children}</>;
}

/** The value label for the condition `text`, or nothing. */
export function PillValueLabel({ text }: { readonly text: string }) {
  const cols = useContext(PillColumns);
  const engine = cols?.engine;
  const resolveByField = cols?.resolveByField;
  const hit = useMemo(() => {
    if (!engine || !resolveByField) return null;
    const parsed = resolveEditPredicate(text, engine, resolveByField);
    return parsed?.resolved
      ? { resolved: parsed.resolved, condition: enrichParsedCondition(parsed.parsed, parsed.resolved, engine).condition }
      : null;
  }, [engine, resolveByField, text]);
  if (!hit || !cols?.connectionId || !cols.database) return null;
  return (
    <FilterValueLabel
      condition={hit.condition}
      column={hit.resolved.column}
      schema={hit.resolved.schema ?? ''}
      table={hit.resolved.table}
      connectionId={cols.connectionId}
      database={cols.database}
      metaInfo={hit.resolved.jsonContext?.metaInfo}
      testId="vqe-pill-value-label"
    />
  );
}
